/**
 * Wheel-in-chat share payload (ISP — "Share a Wheel to a user via Chat").
 *
 * A single, pure builder that turns a wheel record into the message payload sent
 * over the DM send seam (`src/services/chat.js` -> POST DM_MESSAGES). This is the
 * ONE shared payload shape so the wheel Share dialog ("Send via Chat") and the
 * in-chat wheel picker (the chat-wheel-select item) both post an identical
 * `shared_wheel` structure — coordinate here, never diverge.
 *
 * Two fields, deliberately:
 *   - `body`      — a plain-text fallback (wheel name + link). The DM backend
 *                   stores `body` verbatim and echoes it TODAY, so a shared wheel
 *                   is always at least a readable, clickable link even before the
 *                   backend understands the rich `shared_wheel` field. No dead UI.
 *   - `shared_wheel` — the structured card payload (id/name/url/preview/author)
 *                   that MessageBubble renders as a wheel card once the backend
 *                   persists + echoes it. Forward-compatible: an unknown field is
 *                   ignored by the current backend, so nothing breaks meanwhile.
 */

/**
 * Resolve the public URL for a wheel.
 *
 * DELIBERATELY does NOT use `wheel.share_url` — measured live on app-dev
 * (rn-0910-w5-share-wheel-token-in-plain-text, the RN sibling of this same
 * builder), that field is the raw BE OG/redirect endpoint
 * (`http://<api-host>/api/v2/wheel/share/<id>/?s=<64-hex token>`), not a web
 * page — pasting it into a chat message body would leak the API host and a
 * capability token as plain text instead of a normal https link. This app
 * already renders the same wheel at its own `/wheel/:id` route (see
 * `src/routes.js`), so always build that page URL from the current origin.
 */
export const resolveWheelShareUrl = (wheel) => {
  const id = wheel?.id;
  if (id === undefined || id === null || `${id}` === "") return "";
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  return `${origin}/wheel/${id}`;
};

/** Best-effort "First Last" author label from a wheel's user_profile. */
export const wheelAuthorName = (wheel) => {
  const p = wheel?.user_profile;
  if (!p) return "";
  return [p.first_name, p.last_name]
    .filter((part) => part && `${part}`.trim())
    .join(" ")
    .trim();
};

/**
 * Build the chat message payload for sharing `wheel`. Pure — no network, no
 * React. Returns `{ body, shared_wheel }`.
 */
export const buildWheelSharePayload = (wheel) => {
  const id = wheel?.id ?? null;
  const name = (wheel?.wheel_name || "").trim();
  const url = resolveWheelShareUrl(wheel);
  const preview = wheel?.wheel_preview || null;
  const author = wheelAuthorName(wheel);

  // Plain-text body that already works on today's text-only DM backend: the
  // name (when present) on its own line above the link, else just the link.
  const body = name ? `${name}\n${url}` : url;

  return {
    body,
    shared_wheel: { id, name, url, preview, author },
  };
};
